import { type Dispatch, type SetStateAction, useRef } from "react";
import { uniqueId } from "lodash";
import { t } from "@/i18n-js/instance";
import type { MarketingEmailTemplateContentType } from "@/react/types/MarketingEmailTemplates";
import { Loader } from "@circle-react-uikit/Loader";
import { Typography } from "@circle-react-uikit/Typography";
import { useTemplatesInfiniteQuery } from "../hooks/useTemplatesInfiniteQuery";
import type { ContentType } from "./TemplateFilter";
import { TemplateList } from "./TemplateList";

const i18nRoot = "settings.emails.templates";

export const SELECTED_CATEGORY_TO_TEMPLATE_TYPE_MAP = {
  saved_templates: "user",
  all: "all",
  circle_templates: "system",
};

export type SelectedCategory =
  keyof typeof SELECTED_CATEGORY_TO_TEMPLATE_TYPE_MAP;

export const TemplateContents = ({
  selectedCategory,
  onApply,
  contentType,
  setContentType,
}: {
  selectedCategory: SelectedCategory;
  onApply: (
    templateId: number,
    contentType: MarketingEmailTemplateContentType,
  ) => void;
  contentType: ContentType;
  setContentType: Dispatch<SetStateAction<ContentType>>;
}) => {
  const scrollableNodeIdRef = useRef(
    uniqueId("marketing-email-list-templates-body-"),
  );
  const { templates, totalCount, fetchNextPage, hasNextPage, isLoading } =
    useTemplatesInfiniteQuery({
      params: {
        page: 1,
        per_page: 20,
      },
      contentType: contentType,
      templateType: SELECTED_CATEGORY_TO_TEMPLATE_TYPE_MAP[selectedCategory],
    });

  if (isLoading) {
    return <Loader />;
  }

  const renderTemplateList = () => (
    <TemplateList
      scrollableNodeIdRef={scrollableNodeIdRef}
      fetchNextPage={fetchNextPage}
      hasNextPage={Boolean(hasNextPage)}
      templates={templates}
      totalCount={totalCount || 0}
      selectedContentType={contentType}
      setSelectedContentType={setContentType}
      onApply={onApply}
      selectedCategory={selectedCategory}
    />
  );

  return (
    <div className="flex w-full flex-col gap-4 overflow-y-auto px-8 pt-6">
      <Typography.TitleMd>{t([i18nRoot, selectedCategory])}</Typography.TitleMd>
      {renderTemplateList()}
    </div>
  );
};
